import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { format } from 'date-fns';
import { Box, Paper } from '@mui/material';
import { Button } from '@/components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, ChevronRight, Info, Loader2, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { generateMealPlanAsync, getClientById } from '@/lib/apiService';
import {
  AI_MEAL_PLAN_MAX_DAYS,
  AI_MEAL_PLAN_MAX_MEAL_SLOTS,
} from '@/components/meal-planning/MealPlanScheduleFields';
import { showErrorToast, showWarningToast } from '@/utils/toast-utils';
import type {
  DraftGenerationBlueprintSelection,
  GenerateMealPlanRequest,
} from '@/types/meal-plan';
import { MealGenerationShowcase } from '@/components/meal-planning/MealGenerationShowcase';
import { MealTypesIntegrated, MealTypesIntegratedRef } from '@/components/clients/meal-settings/MealTypesIntegrated';
import type { Client, ClientMedicalCondition } from '@/types/client';
import { DietaryIntakeGoals, DietaryIntakeGoalsRef } from '@/clients/details/legacy/components/DietaryIntakeGoals';
import {
  DEFAULT_AI_MEAL_PLAN_COHORT_PAYLOAD,
  type AIModelOption,
} from '@/constants/ai-model-options';
import SearchableClientDropdown from '@/components/shared/SearchableClientDropdown';
import { MealSuggestionsClientSummaryStrip } from '@/components/meal-planning/manual/MealSuggestionsClientSummaryStrip';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import { resolveClientTargetCalories } from '@/utils/client-goals';
import { validateMealPlanCalorieOverride } from '@/utils/meal-plan-calorie-override-validation';
import {
  extractMealPlanDateConflicts,
  formatMealPlanDateConflictMessage,
} from '@/utils/meal-plan-date-conflicts';
import { cn } from '@/lib/utils';
import {
  trackAIMealPlanClientGoalsEditClicked,
  trackAIMealPlanClientGoalsSaved,
  trackAIMealPlanClientSelected,
  trackAIMealPlanDietaryPreferencesEditClicked,
  trackAIMealPlanDietaryPreferencesSaved,
  trackAIMealPlanExitClicked,
  trackAIMealPlanGenerateClicked,
  trackAIMealPlanSetupCompleted,
  trackAIMealPlanSetupViewed,
  trackAIMealPlanSingleSlotWarningCancelClicked,
  trackAIMealPlanSingleSlotWarningContinueClicked,
  trackAIMealPlanSlotAdded,
  trackAIMealPlanSlotRemoved,
  trackAIMealPlanUseClientMealSlotsDisabled,
  trackAIMealPlanUseClientMealSlotsEnabled,
  type AIMealPlanExitClickEvent,
} from '@/lib/analytics/trackers/aiMealPlanSetup';

interface AIMealPlanFormData {
  clientId: string;
  planName: string;
  startDate: string;
  endDate: string;
  additionalText: string;
  aiModel: AIModelOption;
}

type NormalizedDietaryGoals = {
  eerGoalCalories?: number;
  proteinGoalMin?: number;
  proteinGoalMax?: number;
  carbsGoalMin?: number;
  carbsGoalMax?: number;
  fatGoalMin?: number;
  fatGoalMax?: number;
  fiberGoalGrams?: number;
  allergies: string[];
  intolerances: string[];
  cuisineTypes: string[];
  preferences: string[];
};

type NumericGoalField =
  | 'eerGoalCalories'
  | 'proteinGoalMin'
  | 'proteinGoalMax'
  | 'carbsGoalMin'
  | 'carbsGoalMax'
  | 'fatGoalMin'
  | 'fatGoalMax'
  | 'fiberGoalGrams';

type DietaryPreferenceField = 'allergies' | 'intolerances' | 'cuisineTypes' | 'preferences';

type ClinicalBlockingRule = {
  ruleId: string;
  guidelineName: string;
  trigger: string;
  message: string;
  surfacePoint?: string;
  behaviour?: string;
  overridePath?: string;
  citation?: string;
};

type ClinicalBlockNotice = {
  rules: ClinicalBlockingRule[];
};

const NUMERIC_GOAL_FIELDS: NumericGoalField[] = [
  'eerGoalCalories',
  'proteinGoalMin',
  'proteinGoalMax',
  'carbsGoalMin',
  'carbsGoalMax',
  'fatGoalMin',
  'fatGoalMax',
  'fiberGoalGrams',
];

const DIETARY_PREFERENCE_FIELDS: DietaryPreferenceField[] = [
  'allergies',
  'intolerances',
  'cuisineTypes',
  'preferences',
];

const ensureStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const parseOptionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeDietaryGoalsInput = (input: unknown): NormalizedDietaryGoals => {
  const data = input && typeof input === 'object' ? (input as Record<string, unknown>) : {};

  return {
    eerGoalCalories: parseOptionalNumber(data.targetCalories ?? data.eerGoalCalories),
    proteinGoalMin: parseOptionalNumber(data.proteinMin ?? data.proteinGoalMin),
    proteinGoalMax: parseOptionalNumber(data.proteinMax ?? data.proteinGoalMax),
    carbsGoalMin: parseOptionalNumber(data.carbsMin ?? data.carbsGoalMin),
    carbsGoalMax: parseOptionalNumber(data.carbsMax ?? data.carbsGoalMax),
    fatGoalMin: parseOptionalNumber(data.fatMin ?? data.fatGoalMin),
    fatGoalMax: parseOptionalNumber(data.fatMax ?? data.fatGoalMax),
    fiberGoalGrams: parseOptionalNumber(data.fiberGrams ?? data.fiberGoalGrams ?? data.fibreGoalGrams),
    allergies: ensureStringArray(data.allergies),
    intolerances: ensureStringArray(data.foodIntolerances),
    cuisineTypes: ensureStringArray(data.cuisineType ?? data.cuisinePreferences),
    preferences: ensureStringArray(data.preferences ?? data.dietaryPreferences),
  };
};

const normalizeClientBaselineGoals = (client: Client | null): NormalizedDietaryGoals => {
  return {
    eerGoalCalories: parseOptionalNumber(resolveClientTargetCalories(client) ?? client?.eerOverride ?? client?.eer),
    proteinGoalMin: parseOptionalNumber(client?.clientGoals?.protein?.grams?.value),
    proteinGoalMax: parseOptionalNumber(client?.clientGoals?.protein?.grams?.value),
    carbsGoalMin: parseOptionalNumber(client?.clientGoals?.carbohydrates?.grams?.value),
    carbsGoalMax: parseOptionalNumber(client?.clientGoals?.carbohydrates?.grams?.value),
    fatGoalMin: parseOptionalNumber(client?.clientGoals?.totalFat?.grams?.value),
    fatGoalMax: parseOptionalNumber(client?.clientGoals?.totalFat?.grams?.value),
    fiberGoalGrams: parseOptionalNumber(client?.clientGoals?.dietaryFibre?.value),
    allergies: ensureStringArray(client?.allergies),
    intolerances: ensureStringArray(client?.foodIntolerances),
    cuisineTypes: ensureStringArray(client?.cuisineTypesPreferred),
    preferences: ensureStringArray(client?.dietaryPreferences),
  };
};

const areStringArraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;

  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();
  return sortedLeft.every((value, index) => value === sortedRight[index]);
};

const buildOverrideClientGoals = (
  current: NormalizedDietaryGoals,
  baseline: NormalizedDietaryGoals
): GenerateMealPlanRequest['overrideClientGoals'] | undefined => {
  const overrideClientGoals: GenerateMealPlanRequest['overrideClientGoals'] = {};

  NUMERIC_GOAL_FIELDS.forEach((field) => {
    const currentValue = current[field];
    const baselineValue = baseline[field];
    if (currentValue !== undefined && currentValue !== baselineValue) {
      overrideClientGoals[field] = currentValue;
    }
  });

  if (!areStringArraysEqual(current.allergies, baseline.allergies)) {
    overrideClientGoals.allergies = current.allergies;
  }
  if (!areStringArraysEqual(current.cuisineTypes, baseline.cuisineTypes)) {
    overrideClientGoals.cuisineTypes = current.cuisineTypes;
  }
  if (!areStringArraysEqual(current.preferences, baseline.preferences)) {
    overrideClientGoals.preferences = current.preferences;
    overrideClientGoals.dietaryPreferences = current.preferences;
  }

  const hasAnyGoal = Object.keys(overrideClientGoals).length > 0;
  return hasAnyGoal ? overrideClientGoals : undefined;
};

const emptyDietaryGoals: NormalizedDietaryGoals = {
  allergies: [],
  intolerances: [],
  cuisineTypes: [],
  preferences: [],
};

const DAY_IN_MS = 1000 * 60 * 60 * 24;

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDaysToDateOnly(value: string, days: number): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  date.setDate(date.getDate() + days);
  return formatDateInputValue(date);
}

function parseDateOnlyMillis(value: string): number | null {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

function getInclusiveDateRangeDays(startDate: string, endDate: string): number | null {
  const startMs = parseDateOnlyMillis(startDate);
  const endMs = parseDateOnlyMillis(endDate);
  if (startMs == null || endMs == null) return null;
  return Math.floor((endMs - startMs) / DAY_IN_MS) + 1;
}

function dateOnlyStringToDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const ms = parseDateOnlyMillis(value);
  return ms == null ? undefined : new Date(ms);
}

function isDateBeforeDateOnly(date: Date, dateOnly: string): boolean {
  const dateMs = parseDateOnlyMillis(formatDateInputValue(date));
  const compareMs = parseDateOnlyMillis(dateOnly);
  return dateMs != null && compareMs != null && dateMs < compareMs;
}

function isDateAfterDateOnly(date: Date, dateOnly: string): boolean {
  const dateMs = parseDateOnlyMillis(formatDateInputValue(date));
  const compareMs = parseDateOnlyMillis(dateOnly);
  return dateMs != null && compareMs != null && dateMs > compareMs;
}

interface AIMealPlanDatePickerProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  isDateDisabled?: (date: Date) => boolean;
  error?: string;
  placeholder?: string;
  defaultMonth?: Date;
  analyticsEventName?: string;
  analyticsLabel?: string;
}

const AIMealPlanDatePicker: React.FC<AIMealPlanDatePickerProps> = ({
  id,
  label,
  value,
  onChange,
  disabled = false,
  isDateDisabled,
  error,
  placeholder = 'Pick a date',
  defaultMonth,
  analyticsEventName,
  analyticsLabel,
}) => {
  const [open, setOpen] = useState(false);
  const selectedDate = dateOnlyStringToDate(value);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            data-analytics-event={analyticsEventName}
            data-analytics-id={analyticsLabel}
            data-analytics-label={analyticsLabel}
            data-analytics-section="ai_meal_plan_setup"
            className={cn(
              'flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-left text-base font-normal text-foreground shadow-none transition-colors md:text-sm',
              'hover:bg-background focus-visible:border-ring focus-visible:outline-none',
              disabled && 'cursor-not-allowed opacity-50',
              !value && 'text-muted-foreground',
            )}
          >
            <span className="min-w-0 flex-1 truncate">
              {selectedDate ? format(selectedDate, 'yyyy-MM-dd') : placeholder}
            </span>
            <CalendarDays className="ml-2 h-4 w-4 shrink-0 text-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={defaultMonth ?? selectedDate}
            onSelect={(date) => {
              if (!date) return;
              onChange(formatDateInputValue(date));
              setOpen(false);
            }}
            initialFocus
            disabled={isDateDisabled}
            className="pointer-events-auto p-3"
          />
        </PopoverContent>
      </Popover>
      {error ? <p className="text-sm text-red-500">{error}</p> : null}
    </div>
  );
};
const MEAL_PLAN_PROGRESS_TICK_MS = 1000;
const OPENAI_GENERATION_PROGRESS_TIMEOUT_MS = 15 * 60 * 1000;
// Simulated warm-up ramp: over the first 2 minutes, creep up one percent at a
// time to a random cap between 30-40% so the bar never sits idle while we wait
// for the real API percentage to catch up and take over. After that, keep
// creeping slowly up to 90% (never higher) for the rest of the generation
// timeout window, instead of freezing at the initial cap.
const MEAL_PLAN_PROGRESS_SIM_PHASE1_DURATION_MS = 2 * 60 * 1000;
const MEAL_PLAN_PROGRESS_SIM_MIN = 30;
const MEAL_PLAN_PROGRESS_SIM_MAX = 40;
const MEAL_PLAN_PROGRESS_SIM_PHASE2_MAX = 90;
const MEAL_PLAN_PROGRESS_SIM_PHASE2_DURATION_MS =
  OPENAI_GENERATION_PROGRESS_TIMEOUT_MS - MEAL_PLAN_PROGRESS_SIM_PHASE1_DURATION_MS;

const pickSimulatedProgressCap = (): number =>
  MEAL_PLAN_PROGRESS_SIM_MIN +
  Math.floor(Math.random() * (MEAL_PLAN_PROGRESS_SIM_MAX - MEAL_PLAN_PROGRESS_SIM_MIN + 1));

const getSimulatedProgress = (elapsedMs: number, phase1Cap: number): number => {
  const clampedElapsedMs = Math.max(0, elapsedMs);
  if (clampedElapsedMs <= MEAL_PLAN_PROGRESS_SIM_PHASE1_DURATION_MS) {
    const ratio = clampedElapsedMs / MEAL_PLAN_PROGRESS_SIM_PHASE1_DURATION_MS;
    return Math.floor(ratio * phase1Cap);
  }

  const phase2ElapsedMs = clampedElapsedMs - MEAL_PLAN_PROGRESS_SIM_PHASE1_DURATION_MS;
  const phase2Ratio = Math.min(1, phase2ElapsedMs / MEAL_PLAN_PROGRESS_SIM_PHASE2_DURATION_MS);
  return Math.floor(phase1Cap + phase2Ratio * (MEAL_PLAN_PROGRESS_SIM_PHASE2_MAX - phase1Cap));
};
const MEAL_PLAN_PROGRESS_PHASE_MS = 90_000;
const MEAL_PLAN_PROGRESS_PHASES = [
  'Reading client profile…',
  'Picking recipes…',
  'Balancing nutrition…',
  'Almost done…',
] as const;

const getMealPlanGenerationPhase = (elapsedMs: number): typeof MEAL_PLAN_PROGRESS_PHASES[number] => {
  const phaseIndex = Math.min(
    MEAL_PLAN_PROGRESS_PHASES.length - 1,
    Math.max(0, Math.floor(elapsedMs / MEAL_PLAN_PROGRESS_PHASE_MS))
  );
  return MEAL_PLAN_PROGRESS_PHASES[phaseIndex];
};

const clinicalBlockRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const clinicalBlockText = (value: unknown): string => (
  typeof value === 'string' ? value.trim() : ''
);

const fallbackGuidelineName = (ruleId: string): string => {
  const readableRule = ruleId
    .replace(/^BLOCK-/, '')
    .replace(/-\d+$/, '')
    .replace(/-/g, ' ')
    .toLowerCase();
  return readableRule
    ? `${readableRule.charAt(0).toUpperCase()}${readableRule.slice(1)} safety guideline`
    : 'Clinical safety guideline';
};

const extractClinicalBlockNotice = (error: unknown): ClinicalBlockNotice | null => {
  const errorRecord = clinicalBlockRecord(error);
  const response = clinicalBlockRecord(errorRecord?.response);
  const body = clinicalBlockRecord(response?.data);
  const details = clinicalBlockRecord(body?.details);
  const code = clinicalBlockText(details?.code || body?.code);
  if (code !== 'AI_MEAL_PLAN_CLINICAL_RULE_BLOCKED') return null;

  const rawRules = Array.isArray(details?.blockingIssues)
    ? details.blockingIssues
    : Array.isArray(body?.blockingIssues)
      ? body.blockingIssues
      : [];
  const rules = rawRules.flatMap((value): ClinicalBlockingRule[] => {
    const rule = clinicalBlockRecord(value);
    if (!rule) return [];
    const ruleId = clinicalBlockText(rule.ruleId);
    const message = clinicalBlockText(rule.message);
    if (!ruleId && !message) return [];

    return [{
      ruleId: ruleId || 'CLINICAL-SAFETY-RULE',
      guidelineName: clinicalBlockText(rule.guidelineName) || fallbackGuidelineName(ruleId),
      trigger: clinicalBlockText(rule.trigger),
      message: message || 'This request conflicts with an active clinical safety guideline.',
      surfacePoint: clinicalBlockText(rule.surfacePoint) || undefined,
      behaviour: clinicalBlockText(rule.behaviour) || undefined,
      overridePath: clinicalBlockText(rule.overridePath) || undefined,
      citation: clinicalBlockText(rule.citation) || undefined,
    }];
  });

  if (rules.length === 0) return null;
  return { rules };
};

const toDisplayToken = (value: string): string => (
  value
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => {
      if (/^\d+$/.test(part)) return part;
      if (part.length <= 5 && part.toUpperCase() === part) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ')
);

const formatClientDisplayName = (value: string): string => (
  value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      word
        .split('-')
        .map((part) => part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part)
        .join('-')
    )
    .join(' ')
);

const formatDisplayList = (values: string[], limit = values.length): string => (
  values
    .slice(0, limit)
    .map((value) => toDisplayToken(String(value).trim()))
    .filter(Boolean)
    .join(', ')
);

const confirmCalorieOverrideForMealPlan = (client: Client, targetCalories: unknown): boolean => {
  const validation = validateMealPlanCalorieOverride(client, targetCalories);
  if (validation.status === 'ok') return true;

  if (validation.status === 'error') {
    showErrorToast(new Error(validation.message), 'Validation Error');
    return false;
  }

  return window.confirm(`${validation.message}\n\nDo you want to continue creating this meal plan?`);
};

const formatMedicalConditionDetailForApi = (detail: ClientMedicalCondition): string | null => {
  const id = detail.conditionId?.trim() || '';
  const name = detail.conditionName?.trim() || '';
  const subtype = detail.conditionSubtype?.trim() || '';
  const normalizedId = id.toLowerCase();
  const normalizedSubtype = subtype.toLowerCase();

  if (normalizedId === 'other' && name) {
    return name;
  }

  if (normalizedId === 'gestational_diabetes') {
    return 'Gestational Diabetes';
  }

  if (normalizedId === 'diabetes') {
    if (/\b(type[-\s]*1|t1d|type i)\b/.test(normalizedSubtype)) return 'Type 1 Diabetes';
    if (/\b(type[-\s]*2|t2d|type ii)\b/.test(normalizedSubtype)) return 'Type 2 Diabetes';
    if (/\b(gestational|gdm)\b/.test(normalizedSubtype)) return 'Gestational Diabetes';
    if (/\b(pre[-\s]?diabetes|prediabetes)\b/.test(normalizedSubtype)) return 'Prediabetes';
    if (/\b(lada|latent autoimmune)\b/.test(normalizedSubtype)) return 'LADA Diabetes';
    if (/\b(not sure|unknown|unsure)\b/.test(normalizedSubtype)) return name || id;
  }

  if (normalizedId === 'thyroid') {
    const thyroidBase = name || 'Thyroid disorder';
    if (/\b(hashimoto|hashimotos)\b/.test(normalizedSubtype)) return `${thyroidBase} (Hashimoto's)`;
    if (/\b(graves)\b/.test(normalizedSubtype)) return `${thyroidBase} (Graves' disease)`;
    if (/\b(hypothyroid|hypothyroidism|underactive)\b/.test(normalizedSubtype)) return `${thyroidBase} (Hypothyroidism)`;
    if (/\b(hyperthyroid|hyperthyroidism|overactive)\b/.test(normalizedSubtype)) return `${thyroidBase} (Hyperthyroidism)`;
    if (/\b(sub[-\s]?clinical)\b/.test(normalizedSubtype)) return `${thyroidBase} (Subclinical thyroid)`;
    if (/\b(not sure|unknown|unsure)\b/.test(normalizedSubtype)) return thyroidBase;
  }

  if (!id && !name) {
    return subtype || null;
  }

  const base = name || id;
  if (!subtype || base.toLowerCase().includes(normalizedSubtype)) {
    return base;
  }

  return `${base} (${subtype})`;
};

const buildClientMedicalConditionsForApi = (client: Client): string[] => {
  const fromDetails = new Set<string>();
  if (Array.isArray(client.medicalConditionDetails)) {
    for (const detail of client.medicalConditionDetails) {
      const condition = formatMedicalConditionDetailForApi(detail);
      if (condition) fromDetails.add(condition);
    }
  }

  if (fromDetails.size > 0) {
    return [...fromDetails];
  }

  const values = new Set<string>();
  const legacy = typeof client.medicalConditions === 'string' ? client.medicalConditions : '';
  for (const part of legacy.split(',')) {
    const t = part.trim();
    if (t) values.add(t);
  }
  return [...values];
};

/**
 * The profile stores an "Other" selection as the literal token `other` with the
 * real value in a separate free-text field. The API only substitutes the text
 * when the request omits allergies, and this page always sends them, so
 * without this the client's actual allergen never reached the planner.
 */
const replaceOtherSelectionWithText = (values: string[], otherText: unknown): string[] => {
  const text = typeof otherText === 'string' ? otherText.trim() : '';
  return values
    .flatMap((value) => (value.trim().toLowerCase() === 'other'
      ? (text ? text.split(/[;,]/).map((part) => part.trim()).filter(Boolean) : [])
      : [value]))
    .filter((value, index, list) => value.length > 0 && list.indexOf(value) === index);
};

const withClientOtherText = (goals: NormalizedDietaryGoals, client: Client | null): NormalizedDietaryGoals => {
  const record = client as (Client & { allergiesOther?: string | null; foodIntolerancesOther?: string | null }) | null;
  return {
    ...goals,
    allergies: replaceOtherSelectionWithText(goals.allergies, record?.allergiesOther),
    intolerances: replaceOtherSelectionWithText(goals.intolerances, record?.foodIntolerancesOther),
  };
};

const getCurrentDietaryGoals = (refValue: unknown, client: Client | null): NormalizedDietaryGoals => {
  const fromRef = normalizeDietaryGoalsInput(refValue);
  const hasAnyFromRef =
    Object.values(fromRef).some((value) => value !== undefined && !(Array.isArray(value) && value.length === 0));

  if (hasAnyFromRef) {
    return withClientOtherText(fromRef, client);
  }

  if (client) {
    return withClientOtherText(normalizeClientBaselineGoals(client), client);
  }

  return emptyDietaryGoals;
};

type AIMealPlanEntrySource =
  | 'client_details'
  | 'meal_planning_navigation'
  | 'meal_planning_client_view'
  | 'preselected_client_link'
  | 'direct_route';

const getAIMealPlanEntrySource = (
  fromPath: string,
  hasPreselectedClient: boolean
): AIMealPlanEntrySource => {
  const normalizedFromPath = fromPath.trim().toLowerCase();

  if (normalizedFromPath.includes('/details-v2/')) return 'client_details';
  if (normalizedFromPath.startsWith('/meal-planning/client/')) return 'meal_planning_client_view';
  if (normalizedFromPath.startsWith('/meal-planning')) return 'meal_planning_navigation';
  if (hasPreselectedClient) return 'preselected_client_link';
  return 'direct_route';
};

const getChangedNumericGoalFields = (
  current: NormalizedDietaryGoals,
  baseline: NormalizedDietaryGoals
): string[] =>
  NUMERIC_GOAL_FIELDS.filter((field) => (current[field] ?? null) !== (baseline[field] ?? null));

const getChangedDietaryPreferenceFields = (
  current: NormalizedDietaryGoals,
  baseline: NormalizedDietaryGoals
): string[] =>
  DIETARY_PREFERENCE_FIELDS.filter((field) => !areStringArraysEqual(current[field], baseline[field]));

const CreateAIMealPlanPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedClientId = searchParams.get('clientId') || '';
  const fromPath = searchParams.get('from')?.trim() || '';
  const requestedPlanName = searchParams.get('name')?.trim() || '';
  const requestedDaysRaw = Number(searchParams.get('days'));
  const requestedDays =
    Number.isFinite(requestedDaysRaw) && requestedDaysRaw > 0
      ? Math.min(AI_MEAL_PLAN_MAX_DAYS, Math.max(1, Math.floor(requestedDaysRaw)))
      : AI_MEAL_PLAN_MAX_DAYS;
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cookingMinutes, setCookingMinutes] = useState(30);
  const [extraKitchenEquipment, setExtraKitchenEquipment] = useState<string[]>([]);
  const [regionalStylesInput, setRegionalStylesInput] = useState('');
  const [generationProgressStartMs, setGenerationProgressStartMs] = useState<number | null>(null);
  const [generationProgressNowMs, setGenerationProgressNowMs] = useState(() => Date.now());
  const [generationProgressFromApi, setGenerationProgressFromApi] = useState<number | null>(null);
  const [generationBlueprint, setGenerationBlueprint] = useState<DraftGenerationBlueprintSelection | null>(null);
  const generationSimCapRef = useRef<number>(MEAL_PLAN_PROGRESS_SIM_MAX);
  const [singleMealSlotPopupOpen, setSingleMealSlotPopupOpen] = useState(false);
  const [pendingGenerateData, setPendingGenerateData] = useState<AIMealPlanFormData | null>(null);
  const [clinicalBlockNotice, setClinicalBlockNotice] = useState<ClinicalBlockNotice | null>(null);
  const [clinicalBlockDialogOpen, setClinicalBlockDialogOpen] = useState(false);
  const [isLoadingClient, setIsLoadingClient] = useState(!!preSelectedClientId);
  const [loadingClientDetails, setLoadingClientDetails] = useState(false);
  const [clientDetails, setClientDetails] = useState<Client | null>(null);
  const [generationDietaryGoals, setGenerationDietaryGoals] = useState<NormalizedDietaryGoals | null>(null);
  const [useClientMealSlots, setUseClientMealSlots] = useState(false);
  const mealTypesRef = useRef<MealTypesIntegratedRef>(null);
  const dietaryGoalsRef = useRef<DietaryIntakeGoalsRef>(null);
  const isMountedRef = useRef(true);
  const setupViewedTrackedRef = useRef(false);
  const clientSelectedInSetupRef = useRef(false);
  const clientGoalsEditorOpenedRef = useRef(false);
  const dietaryPreferencesEditorOpenedRef = useRef(false);
  const mealSlotAddedInSetupRef = useRef(false);
  const clientMealSlotsEnabledRef = useRef(false);
  const clientMealSlotsDisabledRef = useRef(false);

  const aiMealPlanEntrySource = useMemo(
    () => getAIMealPlanEntrySource(fromPath, Boolean(preSelectedClientId)),
    [fromPath, preSelectedClientId]
  );

  const getAIMealPlanBaseProperties = useCallback(() => ({
    ai_meal_plan_entry_source: aiMealPlanEntrySource,
    has_preselected_client: Boolean(preSelectedClientId),
    meal_plan_flow: 'ai',
    meal_plan_surface: 'ai_setup',
    product_area: 'meal_plans',
    requested_days: requestedDays,
    requested_plan_name_provided: Boolean(requestedPlanName),
  }), [aiMealPlanEntrySource, preSelectedClientId, requestedDays, requestedPlanName]);

  const today = useMemo(() => formatDateInputValue(new Date()), []);
  const defaultEndDate = useMemo(
    () => addDaysToDateOnly(today, requestedDays - 1),
    [requestedDays, today],
  );

  const { control, handleSubmit, formState: { errors }, watch, setValue, getValues } = useForm<AIMealPlanFormData>({
    defaultValues: {
      clientId: preSelectedClientId,
      planName: requestedPlanName,
      startDate: today,
      endDate: defaultEndDate,
      additionalText: '',
      aiModel: DEFAULT_AI_MEAL_PLAN_COHORT_PAYLOAD.defaultModel,
    },
    mode: 'onChange',
  });

  const clientId = watch('clientId');
  const aiModel = watch('aiModel');
  const startDateValue = watch('startDate');
  const endDateValue = watch('endDate');

  const maxSelectableEndDate = useMemo(() => {
    if (!startDateValue) return undefined;
    return addDaysToDateOnly(startDateValue, AI_MEAL_PLAN_MAX_DAYS - 1);
  }, [startDateValue]);
  const selectedDateRangeDays = useMemo(() => {
    if (!startDateValue || !endDateValue) return null;
    return getInclusiveDateRangeDays(startDateValue, endDateValue);
  }, [endDateValue, startDateValue]);
  const isDateRangeOverLimit = selectedDateRangeDays != null && selectedDateRangeDays > AI_MEAL_PLAN_MAX_DAYS;

  const isStartDateDisabled = useCallback(
    (date: Date) => isDateBeforeDateOnly(date, today),
    [today],
  );

  const isEndDateDisabled = useCallback(
    (date: Date) => {
      if (!startDateValue || !maxSelectableEndDate) return true;
      if (isDateBeforeDateOnly(date, today)) return true;
      if (isDateBeforeDateOnly(date, startDateValue)) return true;
      if (isDateAfterDateOnly(date, maxSelectableEndDate)) return true;
      return false;
    },
    [maxSelectableEndDate, startDateValue, today],
  );

  useEffect(() => {
    if (!loading) {
      setGenerationProgressStartMs(null);
      setGenerationProgressFromApi(null);
      setGenerationBlueprint(null);
      return;
    }

    const startedAt = Date.now();
    generationSimCapRef.current = pickSimulatedProgressCap();
    setGenerationProgressStartMs(startedAt);
    setGenerationProgressNowMs(startedAt);
  }, [loading]);

  useEffect(() => {
    if (!loading || generationProgressStartMs === null) return;

    const intervalId = window.setInterval(() => {
      setGenerationProgressNowMs(Date.now());
    }, MEAL_PLAN_PROGRESS_TICK_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [generationProgressStartMs, loading]);

  const generationProgressPhase = useMemo(() => {
    if (!loading || generationProgressStartMs === null) return 0;

    const elapsedMs = Math.max(0, generationProgressNowMs - generationProgressStartMs);
    return getMealPlanGenerationPhase(elapsedMs);
  }, [generationProgressNowMs, generationProgressStartMs, loading]);

  const simulatedProgressValue = useMemo(() => {
    if (!loading || generationProgressStartMs === null) return 0;
    const elapsedMs = generationProgressNowMs - generationProgressStartMs;
    return getSimulatedProgress(elapsedMs, generationSimCapRef.current);
  }, [generationProgressNowMs, generationProgressStartMs, loading]);

  // Show whichever is further along: the simulated warm-up ramp or the real
  // API-reported percentage (which takes over once it overtakes the ramp).
  const generationProgressValue = Math.max(simulatedProgressValue, generationProgressFromApi ?? 0);
  const generationProgressSummary =
    generationProgressPhase === 0 ? MEAL_PLAN_PROGRESS_PHASES[0] : generationProgressPhase;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const selectedClientName = useMemo(() => {
    if (!clientDetails) return 'your client';
    const fullName = [clientDetails.firstName, clientDetails.lastName].filter(Boolean).join(' ').trim();
    return fullName ? formatClientDisplayName(fullName) : clientDetails.email || 'your client';
  }, [clientDetails]);

  const clientHighlights = useMemo(() => {
    if (!clientDetails) {
      return 'Select a client to load goals, dietary preferences, and meal slots.';
    }

    const highlights: string[] = [];
    const conditionText = typeof clientDetails.medicalConditions === 'string'
      ? clientDetails.medicalConditions.trim()
      : '';
    const conditionValues = conditionText
      ? conditionText.split(',').map((part) => toDisplayToken(part.trim())).filter(Boolean)
      : [];

    if (conditionValues.length > 0) {
      highlights.push(`Medical conditions: ${conditionValues.join(', ')}`);
    }
    const dietaryContext = generationDietaryGoals ?? normalizeClientBaselineGoals(clientDetails);

    if (dietaryContext.allergies.length > 0) {
      highlights.push(`Allergies: ${formatDisplayList(dietaryContext.allergies)}`);
    }
    if (dietaryContext.intolerances.length > 0) {
      highlights.push(`Intolerances: ${formatDisplayList(dietaryContext.intolerances)}`);
    }
    if (dietaryContext.cuisineTypes.length > 0) {
      highlights.push(`Cuisines: ${formatDisplayList(dietaryContext.cuisineTypes)}`);
    }
    if (dietaryContext.preferences.length > 0) {
      highlights.push(`Preferences: ${formatDisplayList(dietaryContext.preferences, 2)}`);
    }

    return highlights.length > 0
      ? highlights.join(' · ')
      : 'Ready to configure calories, macros, and meal slots.';
  }, [clientDetails, generationDietaryGoals]);

  useEffect(() => {
    if (setupViewedTrackedRef.current) return;
    setupViewedTrackedRef.current = true;
    trackAIMealPlanSetupViewed(getAIMealPlanBaseProperties());
  }, [getAIMealPlanBaseProperties]);

  const targetCalories = useMemo(() => {
    const raw = clientDetails?.eerOverride ?? clientDetails?.eer;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? Math.round(value) : null;
  }, [clientDetails]);

  const handleClientSelected = useCallback(
    async (selectedClientId: string, client: Client) => {
      if (!selectedClientId) {
        setClientDetails(null);
        return;
      }

      clientSelectedInSetupRef.current = true;
      trackAIMealPlanClientSelected({
        ...getAIMealPlanBaseProperties(),
        client_selected_in_setup: true,
      });

      setLoadingClientDetails(true);
      setClientDetails(null);

      try {
        const response = await getClientById(selectedClientId);
        if (response.client) {
          setClientDetails(response.client as Client);
        } else {
          setClientDetails(client);
        }
      } catch (error) {
        console.error('Error loading selected client details:', error);
        setClientDetails(client);
      } finally {
        setLoadingClientDetails(false);
      }
    },
    [getAIMealPlanBaseProperties]
  );

  const refreshClientData = useCallback(async () => {
    if (!clientId) return;

    try {
      const response = await getClientById(clientId, { force: true });
      if (response.client) {
        setClientDetails(response.client as Client);
      }
    } catch (error) {
      console.error('Error refreshing client details:', error);
    }
  }, [clientId]);

  // Load pre-selected client info
  useEffect(() => {
    if (preSelectedClientId) {
      const loadClient = async () => {
        setIsLoadingClient(true);
        setLoadingClientDetails(true);
        try {
          const response = await getClientById(preSelectedClientId);
          if (response.client) {
            const detailedClient = response.client as Client;
            setValue('clientId', preSelectedClientId, {
              shouldDirty: true,
              shouldValidate: true,
            });
            setClientDetails(detailedClient);
          }
        } catch (error) {
          console.error('Error loading pre-selected client:', error);
          showErrorToast(error, "Error Loading Client");
        } finally {
          setIsLoadingClient(false);
          setLoadingClientDetails(false);
        }
      };
      loadClient();
    }
  }, [preSelectedClientId, setValue]);

  const handleClientGoalsEditOpen = useCallback(() => {
    clientGoalsEditorOpenedRef.current = true;
    trackAIMealPlanClientGoalsEditClicked({
      ...getAIMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getAIMealPlanBaseProperties, getValues]);

  const handleDietaryPreferencesEditOpen = useCallback(() => {
    dietaryPreferencesEditorOpenedRef.current = true;
    trackAIMealPlanDietaryPreferencesEditClicked({
      ...getAIMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getAIMealPlanBaseProperties, getValues]);

  const handleClientGoalsSaved = useCallback(() => {
    trackAIMealPlanClientGoalsSaved({
      ...getAIMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getAIMealPlanBaseProperties, getValues]);

  const handleDietaryPreferencesSaved = useCallback(() => {
    trackAIMealPlanDietaryPreferencesSaved({
      ...getAIMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getAIMealPlanBaseProperties, getValues]);

  const handleUseClientMealSlotsChange = useCallback((checked: boolean) => {
    setUseClientMealSlots(checked);
    if (checked) {
      clientMealSlotsEnabledRef.current = true;
      trackAIMealPlanUseClientMealSlotsEnabled({
        ...getAIMealPlanBaseProperties(),
        client_selected_in_setup: clientSelectedInSetupRef.current,
        has_current_client: Boolean(getValues('clientId')),
      });
      return;
    }

    clientMealSlotsDisabledRef.current = true;
    trackAIMealPlanUseClientMealSlotsDisabled({
      ...getAIMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getAIMealPlanBaseProperties, getValues]);

  const handleMealSlotAdded = useCallback(() => {
    mealSlotAddedInSetupRef.current = true;
    trackAIMealPlanSlotAdded({
      ...getAIMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getAIMealPlanBaseProperties, getValues]);

  const handleMealSlotRemoved = useCallback(() => {
    trackAIMealPlanSlotRemoved({
      ...getAIMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });
  }, [getAIMealPlanBaseProperties, getValues]);

  const handleBack = useCallback((eventName: AIMealPlanExitClickEvent = 'AI Meal Plan Back Clicked') => {
    trackAIMealPlanExitClicked(eventName, {
      ...getAIMealPlanBaseProperties(),
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(getValues('clientId')),
    });

    if (fromPath) {
      navigate(fromPath);
      return;
    }

    navigate(-1);
  }, [fromPath, getAIMealPlanBaseProperties, getValues, navigate]);

  const proceedGenerateMealPlan = async (data: AIMealPlanFormData) => {
    if (!clientDetails) {
      showErrorToast(new Error('Please select a client first.'), 'Client Required');
      return;
    }

    setClinicalBlockNotice(null);
    setClinicalBlockDialogOpen(false);

    try {
      console.log('Generating AI meal plan with data:', data);

      const days = getInclusiveDateRangeDays(data.startDate, data.endDate);
      if (days == null || days < 1) {
        showWarningToast('Validation Error', 'Please select a valid date range.');
        return;
      }
      if (days > AI_MEAL_PLAN_MAX_DAYS) {
        showWarningToast(
          'Validation Error',
          `AI meal plans cannot exceed ${AI_MEAL_PLAN_MAX_DAYS} days.`,
        );
        return;
      }

      const dietaryGoals = getCurrentDietaryGoals(dietaryGoalsRef.current?.getCurrentValues(), clientDetails);
      if (!confirmCalorieOverrideForMealPlan(clientDetails, dietaryGoals.eerGoalCalories)) {
        return;
      }

      setGenerationDietaryGoals(dietaryGoals);

      const baselineGoals = normalizeClientBaselineGoals(clientDetails);
      const overrideClientGoals = buildOverrideClientGoals(dietaryGoals, baselineGoals);
      const changedClientGoalFields = getChangedNumericGoalFields(dietaryGoals, baselineGoals);
      const changedDietaryPreferenceFields = getChangedDietaryPreferenceFields(dietaryGoals, baselineGoals);
      const clientGoalsChanged = changedClientGoalFields.length > 0;
      const dietaryPreferencesChanged = changedDietaryPreferenceFields.length > 0;
      const mealTypes = useClientMealSlots ? (mealTypesRef.current?.getCurrentMealTypes() || []) : [];
      const additionalGuidance = data.additionalText?.trim() || '';
      const aiModelChangedFromDefault = false;
      const aiMealPlanSetupChanged =
        clientGoalsChanged ||
        dietaryPreferencesChanged ||
        useClientMealSlots ||
        clientMealSlotsEnabledRef.current ||
        clientMealSlotsDisabledRef.current ||
        mealSlotAddedInSetupRef.current ||
        Boolean(additionalGuidance) ||
        aiModelChangedFromDefault;
      const directClientSelectToGenerate =
        clientSelectedInSetupRef.current &&
        !aiMealPlanSetupChanged;
      const directPreselectedClientToGenerate =
        Boolean(preSelectedClientId) &&
        !clientSelectedInSetupRef.current &&
        !aiMealPlanSetupChanged;
      let overrideMealProgram: GenerateMealPlanRequest['overrideMealProgram'] | undefined;

      if (useClientMealSlots) {
        if (mealTypes.length > AI_MEAL_PLAN_MAX_MEAL_SLOTS) {
          showWarningToast(
            'Validation Error',
            `AI meal plans support up to ${AI_MEAL_PLAN_MAX_MEAL_SLOTS} meal slots. Remove extra slots before generating.`,
          );
          return;
        }

        const hasMissingRequired = mealTypes.some(
          (meal) => !meal.name?.trim() || !meal.timeSlot?.trim() || !meal.mealType
        );
        if (hasMissingRequired) {
          showWarningToast(
            'Validation Error',
            'Please ensure all meal slots have a Name, Type, and Time filled out.'
          );
          return;
        }

        const mealsWithKcal = mealTypes.filter((meal) =>
          meal.targetKcal !== null && meal.targetKcal !== undefined && meal.targetKcal > 0
        );
        const mealsWithoutKcal = mealTypes.filter((meal) =>
          meal.targetKcal === null || meal.targetKcal === undefined || meal.targetKcal <= 0
        );

        if (mealsWithKcal.length > 0 && mealsWithoutKcal.length > 0) {
          showWarningToast(
            'Validation Error',
            'Either all meal slots should have target calories or none of them should. Please provide values for all meals or leave all empty.'
          );
          return;
        }

        overrideMealProgram = {
          meals: mealTypes.map((meal, index) => ({
            mealOrder: index + 1,
            mealName: meal.name,
            mealTime: meal.timeSlot,
            mealType: meal.mealType,
            ...(meal.targetKcal !== null && meal.targetKcal !== undefined && meal.targetKcal > 0
              ? { targetCalories: meal.targetKcal }
              : {}),
          })),
        };
      }

      const medicalConditions = buildClientMedicalConditionsForApi(clientDetails);

      setIsSubmitting(true);

      const response = await generateMealPlanAsync(
        data.clientId,
        data.startDate,
        days,
        undefined,
        data.aiModel,
        {
          name: data.planName?.trim() || undefined,
          kitchenProfile: { maxMealMinutes: cookingMinutes, maxSnackMinutes: 10,
            equipment: ['hob', 'saucepan', 'frying pan', 'bowl', 'knife', 'spoon', 'kettle', 'sieve', ...extraKitchenEquipment] },
          cuisineRegions: regionalStylesInput.split(',').map((value) => value.trim()).filter(Boolean).slice(0, 6),
          allergies: dietaryGoals.allergies,
          intolerances: dietaryGoals.intolerances,
          cuisineTypes: dietaryGoals.cuisineTypes,
          preferences: dietaryGoals.preferences,
          dietaryPreferences: dietaryGoals.preferences,
          ...(medicalConditions.length > 0 ? { medicalConditions } : {}),
          overrideClientGoals,
          overrideMealProgram,
        }
      );

      console.log('AI meal plan API response:', response);

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to generate meal plan');
      }

      const topLevelResponse = response as unknown as { draftId?: string; id?: string };
      const draftId =
        response.data?.draftId ??
        response.data?.id ??
        topLevelResponse.draftId ??
        topLevelResponse.id;

      if (!draftId) {
        throw new Error(response.message || 'Failed to start meal plan generation.');
      }

      trackAIMealPlanSetupCompleted({
        ...getAIMealPlanBaseProperties(),
        additional_guidance_length: additionalGuidance.length,
        additional_guidance_provided: Boolean(additionalGuidance),
        ai_meal_plan_setup_changed: aiMealPlanSetupChanged,
        ai_model: data.aiModel,
        ai_model_changed_from_default: aiModelChangedFromDefault,
        client_goal_changed_fields: changedClientGoalFields,
        client_goals_changed: clientGoalsChanged,
        client_goals_editor_opened: clientGoalsEditorOpenedRef.current,
        client_meal_slots_disabled_in_setup: clientMealSlotsDisabledRef.current,
        client_meal_slots_enabled_in_setup: clientMealSlotsEnabledRef.current,
        client_selected_in_setup: clientSelectedInSetupRef.current,
        dietary_preference_changed_fields: changedDietaryPreferenceFields,
        dietary_preferences_changed: dietaryPreferencesChanged,
        dietary_preferences_editor_opened: dietaryPreferencesEditorOpenedRef.current,
        direct_client_select_to_generate: directClientSelectToGenerate,
        direct_preselected_client_to_generate: directPreselectedClientToGenerate,
        from_client_details_page: aiMealPlanEntrySource === 'client_details',
        from_meal_planning_navigation: aiMealPlanEntrySource === 'meal_planning_navigation',
        has_override_client_goals: Boolean(overrideClientGoals),
        has_override_meal_program: Boolean(overrideMealProgram),
        meal_slot_count: mealTypes.length,
        meal_slots_added_in_setup: mealSlotAddedInSetupRef.current,
        plan_duration_days: days,
        plan_name_provided: Boolean(data.planName?.trim()),
        use_client_meal_slots: useClientMealSlots,
      });

      setIsSubmitting(false);
      setLoading(true);

      // The draft page polls and previews completed days while generation continues.
      const initialStatus = response.data?.generationState || response.data?.status;

      const draftPath = `/ai-meal-plan/draft/${draftId}/client/${data.clientId}`;
      console.log('Navigating to AI draft:', draftPath);
      navigate(draftPath, {
        state: {
          from: fromPath || '/ai-meal-planning',
          clientId: data.clientId,
          planName: data.planName?.trim() || undefined,
          initialStatus,
          message: response.message,
          aiProvider: data.aiModel,
          startDate: data.startDate,
          endDate: data.endDate,
          days,
          requestedDietaryContext: {
            allergies: dietaryGoals.allergies,
            intolerances: dietaryGoals.intolerances,
            cuisineTypes: dietaryGoals.cuisineTypes,
            preferences: dietaryGoals.preferences,
            dietaryPreferences: dietaryGoals.preferences,
          },
        },
      });
    } catch (error) {
      console.error('Error generating AI meal plan:', error);
      if (isMountedRef.current) {
        setIsSubmitting(false);
        setLoading(false);
      }
      const dateConflicts = extractMealPlanDateConflicts(error);
      if (dateConflicts.length > 0) {
        showWarningToast('Date conflict', formatMealPlanDateConflictMessage(dateConflicts));
        return;
      }
      const blockNotice = extractClinicalBlockNotice(error);
      if (blockNotice) {
        setClinicalBlockNotice(blockNotice);
        return;
      }
      showErrorToast(error, "Generation Failed");
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
        setLoading(false);
      }
    }
  };

  const onSubmit = async (data: AIMealPlanFormData) => {
    trackAIMealPlanGenerateClicked({
      ...getAIMealPlanBaseProperties(),
      ai_model: data.aiModel,
      client_selected_in_setup: clientSelectedInSetupRef.current,
      has_current_client: Boolean(data.clientId),
      plan_name_provided: Boolean(data.planName?.trim()),
      use_client_meal_slots: useClientMealSlots,
    });

    if (useClientMealSlots) {
      const mealTypes = mealTypesRef.current?.getCurrentMealTypes() || [];
      if (mealTypes.length > AI_MEAL_PLAN_MAX_MEAL_SLOTS) {
        showWarningToast(
          'Validation Error',
          `AI meal plans support up to ${AI_MEAL_PLAN_MAX_MEAL_SLOTS} meal slots. Remove extra slots before generating.`,
        );
        return;
      }

      if (mealTypes.length === 0) {
        showErrorToast(
          new Error('Meal slot not added. Please add at least one meal slot before generating.'),
          'Validation Error'
        );
        return;
      }

      if (mealTypes.length === 1) {
        setPendingGenerateData(data);
        setSingleMealSlotPopupOpen(true);
        return;
      }
    }

    await proceedGenerateMealPlan(data);
  };

  const handleSingleMealSlotConfirm = async () => {
    if (!pendingGenerateData) {
      setSingleMealSlotPopupOpen(false);
      return;
    }

    const queuedData = pendingGenerateData;
    setSingleMealSlotPopupOpen(false);
    setPendingGenerateData(null);
    await proceedGenerateMealPlan(queuedData);
  };

  const activeStepIndex = loading ? 1 : 0;

  return (
    <Box data-demo-id="ai-plan-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className={loading ? 'flex min-h-0 flex-1 flex-col p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7' : 'space-y-6 p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7'}>
          <div className="bg-transparent">
            <div className="border-b border-slate-200 px-4 py-3 sm:px-6 lg:px-7">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto w-fit gap-1 px-0 text-base font-medium text-slate-600 hover:bg-transparent hover:text-slate-800"
                    onClick={() => handleBack('AI Meal Plan Back Clicked')}
                    data-analytics-ignore="true"
                  >
                    <ArrowLeft className="h-3 w-3" />
                    Go Back
                  </Button>
                  <span className="hidden h-6 w-px bg-slate-300 md:block" />
                  <div className="md:pl-2">
                    <h1 className="text-lg font-semibold text-slate-900">AI Meal Plan Generator</h1>
                    <p className="text-xs text-slate-500">
                      Create personalized meal plans for{' '}
                      <ClientNameLink clientId={clientId} className="text-slate-600 hover:text-slate-800">
                        {selectedClientName}
                      </ClientNameLink>
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2" />
              </div>
            </div>

            <div className="border-b border-slate-200 px-4 py-3 sm:px-6 lg:px-7">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4 w-full">
                {['Setup', 'Generate', 'Select and Review', 'Approve and Assign'].map((step, index) => (
                  <React.Fragment key={step}>
                    <div className="flex shrink-0 items-center gap-2">
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                          index <= activeStepIndex ? 'bg-cyan-500 text-white' : 'bg-slate-200 text-slate-600'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span
                        className={`text-base leading-tight ${
                          index <= activeStepIndex ? 'font-medium text-slate-800' : 'text-slate-400'
                        }`}
                      >
                        {step}
                      </span>
                    </div>
                    {index < 3 && (
                      <div
                        className={`hidden h-px flex-1 md:block ${
                          index < activeStepIndex ? 'bg-cyan-500' : 'bg-slate-300'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {isLoadingClient ? (
            <Card>
              <CardContent className="flex items-center justify-center py-16">
                <Loader2 className="mr-3 h-8 w-8 animate-spin text-primary" />
                <span className="text-lg text-muted-foreground">Loading client data...</span>
              </CardContent>
            </Card>
          ) : loading ? (
            <div className="flex min-h-[460px] flex-1 items-center justify-center px-4 py-6">
              <MealGenerationShowcase
                heading={
                  <>
                    Generating meals for{' '}
                    <ClientNameLink
                      clientId={clientId}
                      className="text-white underline-offset-2 hover:text-white hover:underline focus-visible:text-white"
                    >
                      {selectedClientName}
                    </ClientNameLink>
                  </>
                }
                subheading={clientHighlights || undefined}
                blueprint={generationBlueprint}
                progress={generationProgressValue}
                phaseLabel={generationProgressSummary}
              />
            </div>
          ) : (
            <CardContent className="p-0">
              <div className="space-y-6">
                {(!preSelectedClientId || !clientDetails) && (
                  <section className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="space-y-2">
                      <Label htmlFor="ai-plan-client">Select Client</Label>
                      <Controller
                        name="clientId"
                        control={control}
                        rules={{ required: 'Please select a client' }}
                        render={({ field }) => (
                          <SearchableClientDropdown
                            value={field.value}
                            onChange={(selectedClientId, selectedClient) => {
                              field.onChange(selectedClientId);
                              handleClientSelected(selectedClientId, selectedClient);
                            }}
                            placeholder="Search and select a client..."
                            error={errors.clientId?.message as string | undefined}
                          />
                        )}
                      />
                      {errors.clientId && (
                        <p className="text-sm text-red-500">{errors.clientId.message}</p>
                      )}
                    </div>
                  </section>
                )}

                {clientId?.trim() ? (
                  <div data-demo-id="ai-plan-client-summary">
                    <MealSuggestionsClientSummaryStrip
                      client={clientDetails}
                      clientId={clientId}
                    />
                  </div>
                ) : null}

                <section data-demo-id="ai-plan-dates" className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="space-y-4">
                    <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
                      <CalendarDays className="h-4 w-4 text-cyan-600" />
                      Plan Date Range
                    </h2>
                    <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                      AI meal plans are limited to {AI_MEAL_PLAN_MAX_DAYS} days.
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <Controller
                        name="startDate"
                        control={control}
                        rules={{
                          required: 'Please select a start date',
                          validate: (value) => {
                            const selectedDate = new Date(`${value.slice(0, 10)}T00:00:00`);
                            const todayDate = new Date(`${today}T00:00:00`);
                            if (selectedDate < todayDate) {
                              return 'Start date cannot be in the past';
                            }
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <AIMealPlanDatePicker
                            id="ai-plan-start-date"
                            label="Start Date"
                            value={field.value}
                            isDateDisabled={isStartDateDisabled}
                            error={errors.startDate?.message as string | undefined}
                            analyticsEventName="AI Meal Plan Start Date Picker Clicked"
                            analyticsLabel="ai_meal_plan_start_date_picker"
                            onChange={(nextStartDate) => {
                              if (!nextStartDate) {
                                field.onChange(nextStartDate);
                                return;
                              }

                              const durationDays = getInclusiveDateRangeDays(startDateValue, endDateValue);
                              field.onChange(nextStartDate);
                              if (durationDays != null) {
                                setValue('endDate', addDaysToDateOnly(nextStartDate, durationDays - 1), {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }
                            }}
                          />
                        )}
                      />

                      <Controller
                        name="endDate"
                        control={control}
                        rules={{
                          required: 'Please select an end date',
                          validate: (value) => {
                            const endDate = new Date(`${value.slice(0, 10)}T00:00:00`);
                            const todayDate = new Date(`${today}T00:00:00`);

                            if (endDate < todayDate) {
                              return 'End date cannot be in the past';
                            }
                            const currentStartDate = getValues('startDate');
                            if (!currentStartDate) {
                              return 'Please select a start date first';
                            }
                            if (parseDateOnlyMillis(currentStartDate) == null) {
                              return 'Please select a start date first';
                            }
                            const startDate = new Date(`${currentStartDate.slice(0, 10)}T00:00:00`);
                            if (endDate < startDate) {
                              return 'End date cannot be before start date';
                            }
                            const durationDays = getInclusiveDateRangeDays(currentStartDate, value);
                            if (durationDays != null && durationDays > AI_MEAL_PLAN_MAX_DAYS) {
                              return `AI meal plans cannot exceed ${AI_MEAL_PLAN_MAX_DAYS} days`;
                            }
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <div className="space-y-2">
                            <AIMealPlanDatePicker
                              id="ai-plan-end-date"
                              label="End Date"
                              value={field.value}
                              disabled={!startDateValue}
                              isDateDisabled={isEndDateDisabled}
                              placeholder={startDateValue ? 'Pick end date' : 'Select start date first'}
                              defaultMonth={dateOnlyStringToDate(startDateValue)}
                              error={isDateRangeOverLimit ? `AI meal plans cannot exceed ${AI_MEAL_PLAN_MAX_DAYS} days` : undefined}
                              analyticsEventName="AI Meal Plan End Date Picker Clicked"
                              analyticsLabel="ai_meal_plan_end_date_picker"
                              onChange={(nextEndDate) => {
                                field.onChange(nextEndDate);
                              }}
                            />
                            {startDateValue && maxSelectableEndDate ? (
                              <p className="text-xs text-muted-foreground">
                                Only {startDateValue} through {maxSelectableEndDate} are selectable (
                                {AI_MEAL_PLAN_MAX_DAYS} days max).
                              </p>
                            ) : null}
                          </div>
                        )}
                      />

                      <Controller
                        name="planName"
                        control={control}
                        render={({ field }) => (
                          <div className="space-y-2">
                            <Label htmlFor="plan-name">Plan Name (Optional)</Label>
                            <Input
                              id="plan-name"
                              placeholder="e.g., 8-Week Metabolic Reset"
                              value={field.value}
                              onChange={field.onChange}
                            />
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </section>

                {clientId && (
                  <>
                    {loadingClientDetails ? (
                      <Card className="border border-border/40">
                        <CardContent className="flex items-center justify-center py-10">
                          <Loader2 className="mr-2 h-5 w-5 animate-spin text-primary" />
                          <span className="text-sm text-muted-foreground">Loading client goals...</span>
                        </CardContent>
                      </Card>
                    ) : (
                      <div data-demo-id="ai-plan-goals">
                        <DietaryIntakeGoals
                          ref={dietaryGoalsRef}
                          clientId={clientId}
                          client={clientDetails || undefined}
                          refreshClientData={refreshClientData}
                          hideSaveButtons={true}
                          compactView={true}
                          onGoalsEditOpen={handleClientGoalsEditOpen}
                          onPreferencesEditOpen={handleDietaryPreferencesEditOpen}
                          onGoalsSaved={handleClientGoalsSaved}
                          onPreferencesSaved={handleDietaryPreferencesSaved}
                        />
                      </div>
                    )}

                    <section data-demo-id="ai-plan-meal-slots" className="space-y-4">
                      <div className="flex items-center gap-3 pl-2" data-analytics-ignore="true">
                        <Checkbox
                          id="use-client-meal-slots"
                          checked={useClientMealSlots}
                          onCheckedChange={(checked) => handleUseClientMealSlotsChange(checked === true)}
                        />
                        <Label htmlFor="use-client-meal-slots" className="cursor-pointer">
                          Use client meal slots
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label="Meal slot distribution info"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                              >
                                <Info className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              Unless you select to use client meal slots or edit them here, AI will decide the meal slots and macro/micronutrient distribution across the day.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      {useClientMealSlots && (
                        <MealTypesIntegrated
                          ref={mealTypesRef}
                          clientId={clientId}
                          hideSaveButtons={true}
                          compactView={true}
                          showSingleMealSlotGuidance={true}
                          maxMealSlots={AI_MEAL_PLAN_MAX_MEAL_SLOTS}
                          onMealSlotAdded={handleMealSlotAdded}
                          onMealSlotRemoved={handleMealSlotRemoved}
                        />
                      )}
                    </section>
                  </>
                )}

                <details className="rounded-2xl border border-slate-200 bg-white p-5">
                  <summary className="cursor-pointer text-sm font-semibold text-slate-800">Cooking preferences (optional)</summary>
                  <p className="mt-3 text-sm text-slate-600">Simple hob cooking, everyday utensils and quick snacks are included.</p>
                  <label className="mt-3 flex items-center gap-3 text-sm">Time per meal
                    <select aria-label="Time per meal" value={cookingMinutes} onChange={(event) => setCookingMinutes(Number(event.target.value))} className="rounded border border-slate-300 p-2">
                      <option value={15}>15 minutes</option><option value={30}>30 minutes</option><option value={45}>45 minutes</option>
                    </select>
                  </label>
                  <div className="mt-3 flex flex-wrap gap-4">{['oven', 'grill', 'blender'].map((equipment) => <label key={equipment} className="flex items-center gap-2 text-sm capitalize">
                    <input type="checkbox" checked={extraKitchenEquipment.includes(equipment)} onChange={(event) => setExtraKitchenEquipment((previous) => event.target.checked ? [...previous, equipment] : previous.filter((item) => item !== equipment))} />{equipment}
                  </label>)}</div>
                  <label className="mt-4 block text-sm">Regional style (optional)
                    <input
                      type="text"
                      aria-describedby="regional-style-help"
                      value={regionalStylesInput}
                      maxLength={200}
                      onChange={(event) => setRegionalStylesInput(event.target.value)}
                      placeholder="e.g. Maharashtrian, Chettinad, Scottish"
                      className="mt-1 w-full rounded border border-slate-300 p-2"
                    />
                  </label>
                  <p id="regional-style-help" className="mt-1 text-xs text-slate-500">Separate styles with commas. Meals lean towards everyday home dishes from these regions, within the cuisines you selected. Allergies and clinical rules always take priority.</p>
                </details>
                {clinicalBlockNotice ? (
                  <button
                    type="button"
                    role="alert"
                    aria-label="Review the clinical rule blocking meal plan generation"
                    className="flex w-full items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-left text-red-950 shadow-sm transition-colors hover:border-red-300 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                    onClick={() => setClinicalBlockDialogOpen(true)}
                  >
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700">
                      <ShieldAlert className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-red-900">Meal plan blocked by a clinical safety check</span>
                      <span className="mt-1 block text-sm leading-5 text-red-800">
                        {clinicalBlockNotice.rules.length === 1
                          ? `${clinicalBlockNotice.rules[0].guidelineName} blocked this request. Review the rule, update the relevant client details, target, or request, and try again.`
                          : `${clinicalBlockNotice.rules.length} clinical safety guidelines blocked this request. Review the rules, update the relevant client details, targets, or request, and try again.`}
                      </span>
                      <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-700">
                        View blocking {clinicalBlockNotice.rules.length === 1 ? 'rule' : 'rules'}
                        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                    </span>
                  </button>
                ) : null}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleBack('AI Meal Plan Cancel Clicked')}
                    data-analytics-ignore="true"
                  >
                    Cancel
                  </Button>
                  <Button
                    data-demo-id="ai-plan-generate"
                    type="button"
                    className="min-w-[210px]"
                    onClick={handleSubmit(onSubmit)}
                    disabled={
                      loading ||
                      isSubmitting ||
                      !clientId ||
                      !aiModel ||
                      isLoadingClient ||
                      loadingClientDetails ||
                      !clientDetails ||
                      !!errors.startDate ||
                      !!errors.endDate ||
                      !!errors.clientId
                    }
                    data-analytics-ignore="true"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating Meal Plan...
                      </>
                    ) : (
                      'Generate AI Meal Plan'
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </div>
      </Paper>

      <AlertDialog
        open={singleMealSlotPopupOpen}
        onOpenChange={(open) => {
          setSingleMealSlotPopupOpen(open);
          if (!open) setPendingGenerateData(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Single meal slot detected</AlertDialogTitle>
            <AlertDialogDescription>
              When only one meal slot is added, all daily calories will be assigned to that single meal.
              Alternatively, you can add a kcal value to each meal slot to generate meals within those calorie limits.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                trackAIMealPlanSingleSlotWarningCancelClicked({
                  ...getAIMealPlanBaseProperties(),
                  client_selected_in_setup: clientSelectedInSetupRef.current,
                  has_current_client: Boolean(getValues('clientId')),
                  use_client_meal_slots: useClientMealSlots,
                });
              }}
              data-analytics-ignore="true"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                trackAIMealPlanSingleSlotWarningContinueClicked({
                  ...getAIMealPlanBaseProperties(),
                  client_selected_in_setup: clientSelectedInSetupRef.current,
                  has_current_client: Boolean(getValues('clientId')),
                  use_client_meal_slots: useClientMealSlots,
                });
                void handleSingleMealSlotConfirm();
              }}
              data-analytics-ignore="true"
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={clinicalBlockDialogOpen} onOpenChange={setClinicalBlockDialogOpen}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Clinical rule blocking generation</AlertDialogTitle>
            <AlertDialogDescription>
              The meal plan was not generated because the request conflicts with the following active safety {clinicalBlockNotice?.rules.length === 1 ? 'guideline' : 'guidelines'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
            {clinicalBlockNotice?.rules.map((rule) => (
              <div key={`${rule.ruleId}-${rule.trigger}`} className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-slate-700">
                <h3 className="font-semibold text-slate-900">{rule.guidelineName}</h3>
                <p className="mt-1 text-xs font-medium uppercase tracking-wide text-red-700">Rule {rule.ruleId}</p>
                <dl className="mt-3 space-y-2">
                  {rule.trigger ? (
                    <div>
                      <dt className="font-medium text-slate-900">What triggered it</dt>
                      <dd className="mt-0.5">{rule.trigger}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="font-medium text-slate-900">Why generation was blocked</dt>
                    <dd className="mt-0.5">{rule.message}</dd>
                  </div>
                  {rule.behaviour ? (
                    <div>
                      <dt className="font-medium text-slate-900">Rule behaviour</dt>
                      <dd className="mt-0.5">{rule.behaviour}</dd>
                    </div>
                  ) : null}
                  {rule.overridePath ? (
                    <div>
                      <dt className="font-medium text-slate-900">Permitted next step</dt>
                      <dd className="mt-0.5">{rule.overridePath}</dd>
                    </div>
                  ) : null}
                  {rule.surfacePoint ? (
                    <div>
                      <dt className="font-medium text-slate-900">Safety checkpoint</dt>
                      <dd className="mt-0.5">{rule.surfacePoint}</dd>
                    </div>
                  ) : null}
                  {rule.citation ? (
                    <div>
                      <dt className="font-medium text-slate-900">Guideline source</dt>
                      <dd className="mt-0.5">{rule.citation}</dd>
                    </div>
                  ) : null}
                </dl>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setClinicalBlockDialogOpen(false)}>
              Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};

export default CreateAIMealPlanPage;
